import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  accountSignInPolicyPartialUpdateMutation,
  accountSignInPolicyRetrieveOptions,
  accountSignInPolicyRetrieveQueryKey
} from 'src/api/main/@tanstack/react-query.gen'
import { useShowApiErrors } from 'src/utils'

export const useSignInPolicySettings = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()

  const signInPolicyQuery = useQuery(accountSignInPolicyRetrieveOptions())

  const signInPolicyMutation = useMutation({
    ...accountSignInPolicyPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(t('account.messages.settingsSaved'))
      await queryClient.invalidateQueries({
        queryKey: accountSignInPolicyRetrieveQueryKey()
      })
    },
    onError: (error) => showApiErrors(error)
  })

  return {
    handleNumber:
      (field: 'refresh_token_lifetime_days' | 'trusted_device_validity_days') =>
      (value: number | null) => {
        if (value == null || value === signInPolicyQuery.data?.[field]) {
          return
        }

        signInPolicyMutation.mutate({ body: { [field]: value } })
      },
    handleToggle:
      (field: 'allow_password_sign_in' | 'allow_social_sign_in') =>
      (checked: boolean) => {
        signInPolicyMutation.mutate({ body: { [field]: checked } })
      },
    hasError: signInPolicyQuery.isError,
    isLoading: signInPolicyQuery.isLoading,
    isPending: signInPolicyMutation.isPending,
    refetch: () => {
      void signInPolicyQuery.refetch()
    },
    signInPolicy: signInPolicyQuery.data
  }
}
